import * as React from "react";
import useTopics from "../hooks/useTopics";
import useCategories from "../hooks/useCategories";
import TopicCreateModal from "../shared/TopicCreateModal";
import TopicEditModal from "../shared/TopicEditModal";
import { Category, Topic } from "../types";
import CategoryCreateModal from "./CategoryCreateModal";
import CategoryEditModal from "./CategoryEditModal";
import _ from "lodash";
import useApi from "@/hooks/useApi";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";

function getArticleCountString(count: number) {
  if (count === 1) return "1 article";
  return `${count} articles`;
}

export default function Index() {
  const { topics, setTopics, fetchTopics, updateTopics, loadingTopics } =
    useTopics();

  const {
    categories,
    fetchCategories,
    loadingCategories,
    addCategory,
    updateCategory,
    removeCategory,
    moveCategoryUp,
    moveCategoryDown
  } = useCategories();

  const [showNewTopicModal, setShowNewTopicModal] = React.useState(false);
  const [showNewCategoryModal, setShowNewCategoryModal] = React.useState(false);
  const [editTopic, setEditTopic] = React.useState<Topic>();
  const [editCategory, setEditCategory] = React.useState<Category>();
  const [showHelp, setShowHelp] = React.useState(false);
  const api = useApi();

  const debouncedUpdateTopics = React.useRef(
    _.debounce((topics: Topic[]) => {
      updateTopics(topics);
    }, 1000)
  );

  const newCategoryDisplayPriority = React.useMemo(() => {
    if (!categories || categories.length === 0) return 0;
    return categories[categories.length - 1].DisplayPriority + 1;
  }, [categories]);

  const sortedCategories = React.useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return [...categories].sort((a, b) => {
      if (a.DisplayPriority !== b.DisplayPriority) {
        return a.DisplayPriority - b.DisplayPriority;
      }
      return a.Name.localeCompare(b.Name);
    });
  }, [categories]);

  const categoryTopicsMap = React.useMemo(() => {
    const map: { [categoryId: string]: Topic[] } = {};
    if (!Array.isArray(topics)) return map;
    topics.forEach((topic) => {
      if (topic.CategoryId !== null) {
        map[topic.CategoryId] = map[topic.CategoryId] || [];
        map[topic.CategoryId].push(topic);
      }
    });
    return map;
  }, [topics]);

  const uncategorizedTopics = React.useMemo(() => {
    if (!Array.isArray(topics)) return [];
    return topics.filter((x) => x.CategoryId === null);
  }, [topics]);

  const deleteTopic = React.useCallback(
    async (topicId: string) => {
      try {
        const resp = await api.admin.delete(
          `api/kb/topics?id=${encodeURIComponent(topicId)}`
        );
        fetchTopics();
        if (!resp.ok) console.error(resp);
      } catch (err) {
        console.error("Failed to delete topic:", err);
      }
    },
    [fetchTopics]
  );

  const renderTopicRow = (topic: Topic) => (
    <div
      key={topic.Id}
      className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <i className="fa fa-bars text-slate-300 text-sm cursor-grab" />
        <div className="min-w-0">
          <span className="text-sm font-medium text-slate-700">
            {topic.Name}
          </span>
          <span className="text-xs text-slate-400 ml-2">
            {getArticleCountString(topic.ArticleCount ?? 0)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {topic.Url && (
          <a
            href={topic.Url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
            title="Open topic page"
            aria-label="Open topic page"
          >
            <i className="fa fa-external-link text-sm" />
          </a>
        )}
        <button
          className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
          onClick={() => setEditTopic(topic)}
          title="Edit topic"
          aria-label="Edit topic"
        >
          <i className="fa fa-pencil text-sm" />
        </button>
        <button
          disabled={(topic.ArticleCount ?? 0) > 0}
          className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
          onClick={() => {
            if (confirm("Are you sure you want to delete this topic?")) {
              deleteTopic(topic.Id);
            }
          }}
          title={
            (topic.ArticleCount ?? 0) > 0
              ? "Remove all articles first"
              : "Delete topic"
          }
          aria-label="Delete topic"
        >
          <i className="fa fa-trash text-sm" />
        </button>
      </div>
    </div>
  );

  if (loadingTopics && loadingCategories) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">
              Topics & Categories
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Organize your knowledge base articles into topics and categories
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SecondaryBlueButton
              label="New Category"
              onClick={() => setShowNewCategoryModal(true)}
            />
            <PrimaryBlueButton
              label="New Topic"
              onClick={() => setShowNewTopicModal(true)}
            />
          </div>
        </div>

        {/* Help Dropdown */}
        <div className="mb-6">
          <button
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setShowHelp(!showHelp)}
          >
            <i
              className={`fa fa-chevron-${showHelp ? "down" : "right"} text-xs`}
            />
            <i className="fa fa-circle-info" />
            How topics and categories work
          </button>
          {showHelp && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-slate-700 space-y-2">
              <p>
                <strong>Categories</strong> are top-level groups that organize
                related topics. Think of them as sections of your help center
                (e.g., "Getting Started", "Billing", "Account").
              </p>
              <p>
                <strong>Topics</strong> sit inside categories and contain the
                actual articles. Each article belongs to exactly one topic (e.g.,
                "Payment Methods" topic under "Billing" category).
              </p>
              <p>
                <strong>Uncategorized topics</strong> appear at the top. Assign
                them to a category when editing the topic.
              </p>
              <p>
                <strong>Ordering:</strong> Use the arrow buttons to reorder
                categories. Topics within a category are ordered by display
                priority.
              </p>
              <p>
                <strong>Bot integration:</strong> When articles have "Enable as
                bot response" turned on, the bot can search by topic to find
                relevant articles and deliver pre-written summaries to customers.
              </p>
            </div>
          )}
        </div>

        {/* Uncategorized Topics */}
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="px-4 py-3 bg-slate-50 border-b rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="fa fa-folder-open text-slate-400 text-sm" />
              <span className="text-sm font-medium text-slate-600">
                Uncategorized
              </span>
              <span className="text-xs text-slate-400">
                ({uncategorizedTopics.length} topic
                {uncategorizedTopics.length !== 1 ? "s" : ""})
              </span>
            </div>
          </div>
          {uncategorizedTopics.length > 0 ? (
            <div className="max-h-64 overflow-y-auto">
              {uncategorizedTopics
                .sort((a, b) => (a.DisplayPriority ?? 0) - (b.DisplayPriority ?? 0))
                .map(renderTopicRow)}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              No uncategorized topics
            </div>
          )}
        </div>

        {/* Categories */}
        {sortedCategories.length === 0 && (
          <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed">
            <i className="fa fa-folder text-3xl text-slate-300 mb-3" />
            <h3 className="text-sm font-medium text-slate-500 mb-1">
              No categories yet
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              Create categories to organize your topics into logical groups
            </p>
            <SecondaryBlueButton
              label="Create First Category"
              onClick={() => setShowNewCategoryModal(true)}
            />
          </div>
        )}

        {sortedCategories.map((category, index) => {
          const catTopics = categoryTopicsMap[category.Id] || [];
          const sortedCatTopics = [...catTopics].sort(
            (a, b) => (a.DisplayPriority ?? 0) - (b.DisplayPriority ?? 0)
          );

          return (
            <div
              key={category.Id}
              className="bg-white rounded-lg border shadow-sm mb-4"
            >
              <div className="px-4 py-3 bg-slate-50 border-b rounded-t-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="fa fa-folder text-blue-500 text-sm" />
                  <span className="text-sm font-semibold text-slate-700">
                    {category.Name}
                  </span>
                  <span className="text-xs text-slate-400">
                    ({catTopics.length} topic
                    {catTopics.length !== 1 ? "s" : ""})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-200 disabled:opacity-30"
                    disabled={index === 0}
                    onClick={() => moveCategoryUp(index)}
                    title="Move up"
                    aria-label="Move category up"
                  >
                    <i className="fa fa-arrow-up text-xs" />
                  </button>
                  <button
                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-200 disabled:opacity-30"
                    disabled={index === sortedCategories.length - 1}
                    onClick={() => moveCategoryDown(index)}
                    title="Move down"
                    aria-label="Move category down"
                  >
                    <i className="fa fa-arrow-down text-xs" />
                  </button>
                  <button
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
                    onClick={() => setEditCategory(category)}
                    title="Edit category"
                    aria-label="Edit category"
                  >
                    <i className="fa fa-pencil text-xs" />
                  </button>
                  <button
                    className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    disabled={catTopics.length > 0}
                    onClick={() => {
                      if (
                        confirm(
                          "Are you sure you want to delete this category?"
                        )
                      ) {
                        removeCategory(category.Id);
                      }
                    }}
                    title={
                      catTopics.length > 0
                        ? "Remove all topics first"
                        : "Delete category"
                    }
                    aria-label="Delete category"
                  >
                    <i className="fa fa-trash text-xs" />
                  </button>
                </div>
              </div>
              {sortedCatTopics.length > 0 ? (
                <div className="max-h-64 overflow-y-auto">
                  {sortedCatTopics.map(renderTopicRow)}
                </div>
              ) : (
                <div className="px-4 py-6 text-center text-sm text-slate-400">
                  No topics in this category yet
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modals */}
      {showNewCategoryModal && (
        <CategoryCreateModal
          displayPriority={newCategoryDisplayPriority}
          onSubmit={(category: Category) => {
            addCategory(category);
            setShowNewCategoryModal(false);
          }}
          onClose={() => setShowNewCategoryModal(false)}
        />
      )}
      {editCategory && (
        <CategoryEditModal
          category={editCategory}
          onSubmit={(category: Category) => {
            updateCategory(category);
            setEditCategory(undefined);
          }}
          onClose={() => setEditCategory(undefined)}
        />
      )}
      {showNewTopicModal && (
        <TopicCreateModal
          onClose={() => setShowNewTopicModal(false)}
          onSubmit={() => {
            setShowNewTopicModal(false);
            fetchTopics();
          }}
        />
      )}
      {editTopic && (
        <TopicEditModal
          topic={editTopic}
          onClose={() => setEditTopic(undefined)}
          onSubmit={() => {
            setEditTopic(undefined);
            fetchTopics();
          }}
        />
      )}
    </>
  );
}
